import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Stethoscope,
  Building2,
  Users,
  Globe2,
  Bell,
  BellOff,
  BellRing,
  Lock,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  User,
  ChevronRight,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Route,
} from 'lucide-react';
import { toast } from 'sonner';
import { departmentLabel, type Doctor, type Department } from '@refdash/api-client';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { usePushNotifications } from '@/hooks/use-push-notifications';
import { isSoundEnabled, setSoundEnabled } from '@/hooks/use-notification-sound';

// ── Sub-page back header ─────────────────────────────────────────────

function SubPageHeader({ title }: { title: string }) {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-3 mb-5"
    >
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      <h2 className="font-sans text-lg font-extrabold text-foreground leading-none">
        {title}
      </h2>
    </motion.div>
  );
}

// ── Settings Menu Page ───────────────────────────────────────────────

export function SettingsPage() {
  const { user, logout } = useAuth();
  const hospitalId = user?.hospital_id ?? '';
  const navigate = useNavigate();

  const { data: doctors = [] } = useQuery({
    queryKey: ['doctors', hospitalId],
    queryFn: () => api.hospitals.listDoctors(hospitalId),
    enabled: !!hospitalId,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments', hospitalId],
    queryFn: () => api.hospitals.listDepartments(hospitalId),
    enabled: !!hospitalId,
  });

  // Tenant administration (reference data, team, intake config) is the rep's
  // surface; everyone else gets personal settings only.
  const isRep = user?.role === 'hospital_rep';
  const menuItems = [
    ...(isRep ? [
      {
        icon: Stethoscope,
        label: 'Doctors',
        subtitle: `${doctors.length} doctor${doctors.length !== 1 ? 's' : ''}`,
        to: '/settings/doctors',
      },
      {
        icon: Building2,
        label: 'Departments',
        subtitle: `${departments.length} department${departments.length !== 1 ? 's' : ''}`,
        to: '/settings/departments',
      },
      {
        icon: Route,
        label: 'Case routing',
        subtitle: 'Send GlobeMed specialties to a department',
        to: '/settings/routing',
      },
      {
        icon: Users,
        label: 'Team',
        subtitle: 'Create and manage hospital users',
        to: '/settings/team',
      },
      {
        icon: Globe2,
        label: 'Intake',
        subtitle: 'Public referral form and sharing',
        to: '/settings/intake',
      },
    ] : []),
    {
      icon: Bell,
      label: 'Notifications',
      to: '/settings/notifications',
    },
    {
      icon: Lock,
      label: 'Change Password',
      to: '/settings/password',
    },
  ];

  return (
    <div className="px-5 py-5 max-w-lg lg:max-w-2xl mx-auto space-y-5">
      {/* Profile card */}
      <Card>
        <CardContent className="flex items-center gap-3 pt-5">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground truncate">
              {user?.display_name}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.username}
            </p>
            {user?.hospital_name && (
              <p className="text-xs text-muted-foreground truncate">
                {user.hospital_name}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Menu items */}
      <Card>
        <CardContent className="p-0">
          {menuItems.map((item, i) => (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              className={cn(
                'flex items-center gap-3 w-full px-4 py-3.5 text-left transition-colors hover:bg-muted/50 active:bg-muted cursor-pointer',
                i !== menuItems.length - 1 && 'border-b border-border',
              )}
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {item.label}
                </p>
                {item.subtitle && (
                  <p className="text-xs text-muted-foreground">
                    {item.subtitle}
                  </p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Sign out */}
      <Card>
        <CardContent className="p-0">
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-4 py-3.5 text-left transition-colors hover:bg-red-50 active:bg-red-100 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
              <LogOut className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-sm font-semibold text-red-600">Sign Out</p>
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Doctors Sub-page ─────────────────────────────────────────────────

export function DoctorsSettingsPage() {
  const { user } = useAuth();
  const hospitalId = user?.hospital_id ?? '';

  return (
    <div className="px-5 py-5 max-w-lg lg:max-w-2xl mx-auto">
      <SubPageHeader title="Doctors" />
      <DoctorsSection hospitalId={hospitalId} />
    </div>
  );
}

function DoctorsSection({ hospitalId }: { hospitalId: string }) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');

  const { data: doctors = [] } = useQuery({
    queryKey: ['doctors', hospitalId],
    queryFn: () => api.hospitals.listDoctors(hospitalId),
    enabled: !!hospitalId,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments', hospitalId],
    queryFn: () => api.hospitals.listDepartments(hospitalId),
    enabled: !!hospitalId,
  });

  const departmentById = new Map(departments.map((d) => [d.id, d]));

  const createMutation = useMutation({
    mutationFn: () =>
      api.hospitals.createDoctor(hospitalId, {
        name,
        name_ar: nameAr,
        department_id: departmentId === '' ? null : departmentId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      cancel();
      toast.success('Doctor added');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (id: number) =>
      api.hospitals.updateDoctor(hospitalId, id, {
        name,
        name_ar: nameAr,
        department_id: departmentId === '' ? null : departmentId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      cancel();
      toast.success('Doctor updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.hospitals.deleteDoctor(hospitalId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      toast.success('Doctor removed');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function startEdit(doc: Doctor) {
    setEditingId(doc.id);
    setName(doc.name);
    setNameAr(doc.name_ar ?? '');
    setDepartmentId(doc.department_id ?? '');
    setIsAdding(false);
  }

  function cancel() {
    setIsAdding(false);
    setEditingId(null);
    setName('');
    setNameAr('');
    setDepartmentId('');
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (editingId) updateMutation.mutate(editingId);
    else createMutation.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-primary" />
            <CardTitle>Doctors</CardTitle>
          </div>
          {!isAdding && editingId === null && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {doctors.map((doc) =>
          editingId === doc.id ? null : (
            <div
              key={doc.id}
              className="flex items-center justify-between py-2 px-1 group"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {doc.name}
                </p>
                {doc.name_ar && (
                  <p className="text-xs text-muted-foreground" dir="rtl">
                    {doc.name_ar}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {doc.department_id != null && departmentById.get(doc.department_id)
                    ? departmentLabel(departmentById.get(doc.department_id)!)
                    : 'No specialty — any department'}
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => startEdit(doc)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => deleteMutation.mutate(doc.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ),
        )}

        {(isAdding || editingId !== null) && (
          <form onSubmit={handleSubmit} className="space-y-2 pt-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Doctor name (English)"
              required
              autoFocus
            />
            <Input
              value={nameAr}
              onChange={(e) => setNameAr(e.target.value)}
              placeholder="اسم الطبيب (Arabic)"
              dir="rtl"
              required
            />
            <select
              className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary"
              value={departmentId}
              onChange={(e) =>
                setDepartmentId(e.target.value ? Number(e.target.value) : '')
              }
              aria-label="Specialty"
            >
              <option value="">Specialty (optional) — any department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {departmentLabel(d)}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={
                  !name || !nameAr || createMutation.isPending || updateMutation.isPending
                }
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingId ? (
                  'Update'
                ) : (
                  'Add'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={cancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {doctors.length === 0 && !isAdding && (
          <p className="text-sm text-muted-foreground py-3 text-center">
            No doctors added yet
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Departments Sub-page ─────────────────────────────────────────────

export function DepartmentsSettingsPage() {
  const { user } = useAuth();
  const hospitalId = user?.hospital_id ?? '';

  return (
    <div className="px-5 py-5 max-w-lg lg:max-w-2xl mx-auto space-y-4">
      <SubPageHeader title="Departments" />
      <DepartmentsSection hospitalId={hospitalId} />
      <RoutingLinkCard />
    </div>
  );
}

/** Departments are only half the job — they receive nothing until a specialty
 * points at them, so send people onward from here rather than leaving them to
 * find Case routing on their own. */
function RoutingLinkCard() {
  const navigate = useNavigate();
  return (
    <Card>
      <CardContent className="flex items-center gap-3 pt-5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Route className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Case routing</p>
          <p className="text-xs text-muted-foreground">
            Choose which GlobeMed specialties reach each department
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/settings/routing')}>
          Open
        </Button>
      </CardContent>
    </Card>
  );
}

function DepartmentsSection({ hospitalId }: { hospitalId: string }) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');

  const { data: departments = [] } = useQuery({
    queryKey: ['departments', hospitalId],
    queryFn: () => api.hospitals.listDepartments(hospitalId),
    enabled: !!hospitalId,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.hospitals.createDepartment(hospitalId, { code, name, name_ar: nameAr }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      cancel();
      toast.success('Department added');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (id: number) =>
      api.hospitals.updateDepartment(hospitalId, id, { code, name, name_ar: nameAr }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      cancel();
      toast.success('Department updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      api.hospitals.deleteDepartment(hospitalId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department removed');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function startEdit(dept: Department) {
    setEditingId(dept.id);
    setCode(dept.code);
    setName(dept.name);
    setNameAr(dept.name_ar ?? '');
    setIsAdding(false);
  }

  function cancel() {
    setIsAdding(false);
    setEditingId(null);
    setCode('');
    setName('');
    setNameAr('');
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (editingId) updateMutation.mutate(editingId);
    else createMutation.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <CardTitle>Departments</CardTitle>
          </div>
          {!isAdding && editingId === null && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {departments.map((dept) =>
          editingId === dept.id ? null : (
            <div
              key={dept.id}
              className="flex items-center justify-between py-2 px-1 group"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {departmentLabel(dept)}
                </p>
                {dept.name_ar && (
                  <p className="text-xs text-muted-foreground" dir="rtl">
                    {dept.name_ar}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => startEdit(dept)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => deleteMutation.mutate(dept.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ),
        )}

        {(isAdding || editingId !== null) && (
          <form onSubmit={handleSubmit} className="space-y-2 pt-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Code (e.g. CARD)"
              maxLength={20}
              required
              autoFocus
            />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="English description (e.g. Cardiology)"
              required
            />
            <Input
              value={nameAr}
              onChange={(e) => setNameAr(e.target.value)}
              placeholder="الوصف بالعربية (Arabic)"
              dir="rtl"
              required
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={
                  !code || !name || !nameAr || createMutation.isPending || updateMutation.isPending
                }
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingId ? (
                  'Update'
                ) : (
                  'Add'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={cancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {departments.length === 0 && !isAdding && (
          <p className="text-sm text-muted-foreground py-3 text-center">
            No departments added yet
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Notifications Sub-page ───────────────────────────────────────────

export function NotificationsSettingsPage() {
  return (
    <div className="px-5 py-5 max-w-lg lg:max-w-2xl mx-auto">
      <SubPageHeader title="Notifications" />
      <NotificationsSection />
    </div>
  );
}

function NotificationsSection() {
  const { requestPermission } = usePushNotifications();
  const [soundOn, setSoundOn] = useState(isSoundEnabled);
  const [permissionState, setPermissionState] = useState<
    'default' | 'granted' | 'denied' | 'unsupported'
  >('default');
  const [requesting, setRequesting] = useState(false);

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true);

  // iOS Safari (non-standalone) doesn't support push — must be added to home screen
  const isIOSSafari =
    /iPhone|iPad/.test(navigator.userAgent) && !isStandalone;

  useEffect(() => {
    if (isIOSSafari) {
      setPermissionState('unsupported');
    } else if (typeof Notification === 'undefined') {
      setPermissionState('unsupported');
    } else {
      setPermissionState(Notification.permission as 'default' | 'granted' | 'denied');
    }
  }, [isIOSSafari]);

  async function handleRequestPermission() {
    setRequesting(true);
    try {
      const result = await requestPermission();
      if (result === 'unsupported') {
        setPermissionState('unsupported');
      } else if (result) {
        setPermissionState(result as 'granted' | 'denied' | 'default');
      }
    } finally {
      setRequesting(false);
    }
  }

  const { data: prefs } = useQuery({
    queryKey: ['push-preferences'],
    queryFn: () => api.push.getPreferences(),
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, boolean>) =>
      api.push.updatePreferences(data),
    onSuccess: () => toast.success('Preferences updated'),
    onError: (err: Error) => toast.error(err.message),
  });

  const toggles = [
    { key: 'new_cases', label: 'New approvals' },
    { key: 'timer_warnings', label: 'Timer warnings' },
    { key: 'case_outcomes', label: 'Case outcomes' },
    { key: 'case_expired', label: 'Expired cases' },
  ] as const;

  return (
    <div className="space-y-4">
      {/* In-app sound */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BellRing className="w-5 h-5 text-primary shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">In-app sound</p>
                <p className="text-xs text-muted-foreground">
                  Play a chime when a new approval arrives
                </p>
              </div>
            </div>
            <Switch
              checked={soundOn}
              onCheckedChange={(checked) => {
                setSoundOn(checked);
                setSoundEnabled(checked);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Push permission card */}
      <Card>
        <CardContent className="pt-5">
          {permissionState === 'unsupported' && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <BellOff className="w-5 h-5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Not supported</p>
                <p className="text-xs">
                  {isIOSSafari
                    ? 'Add this app to your Home Screen first. Tap the share button, then "Add to Home Screen".'
                    : 'Push notifications are not supported on this device.'}
                </p>
              </div>
            </div>
          )}

          {permissionState === 'denied' && (
            <div className="flex items-center gap-3 text-amber-600">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Notifications blocked</p>
                <p className="text-xs text-muted-foreground">
                  Enable notifications in your browser settings to receive push alerts.
                </p>
              </div>
            </div>
          )}

          {permissionState === 'granted' && (
            <div className="flex items-center gap-3 text-emerald-600">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Push notifications enabled</p>
                <p className="text-xs text-muted-foreground">
                  You'll receive alerts even when the app is closed.
                </p>
              </div>
            </div>
          )}

          {permissionState === 'default' && (
            <div className="flex items-center gap-3">
              <BellRing className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Push notifications</p>
                <p className="text-xs text-muted-foreground">
                  Get notified about new approvals and updates.
                </p>
              </div>
              <Button
                size="sm"
                onClick={handleRequestPermission}
                disabled={requesting}
              >
                {requesting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Enable'
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preference toggles — only shown when permission granted */}
      {permissionState === 'granted' && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary" />
              <CardTitle>Notification preferences</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {toggles.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{label}</span>
                <Switch
                  checked={prefs?.[key] ?? true}
                  onCheckedChange={(checked) =>
                    mutation.mutate({ [key]: checked })
                  }
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Password Sub-page ────────────────────────────────────────────────

export function PasswordSettingsPage() {
  return (
    <div className="px-5 py-5 max-w-lg lg:max-w-2xl mx-auto">
      <SubPageHeader title="Change Password" />
      <PasswordSection />
    </div>
  );
}

function PasswordSection() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.settings.changePassword({
        old_password: oldPassword,
        new_password: newPassword,
      }),
    onSuccess: () => {
      toast.success('Password changed');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    mutation.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-primary" />
          <CardTitle>Change Password</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            placeholder="Current password"
            required
          />
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password"
            required
          />
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            required
          />
          <Button
            type="submit"
            size="sm"
            disabled={
              mutation.isPending || !oldPassword || !newPassword || !confirmPassword
            }
          >
            {mutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Update Password'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
